import type { ContractCommentView, SharedObjectView } from "@closer/shared";
import { Button, Card, FormField, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDateTime } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * PAYLAŞILAN YÜZEYDE YORUM (CLOS-886).
 *
 * ### ⚠ NEDEN VAR: VERİLEN İZNİN TÜKETİCİSİ YOKTU
 *
 * Sahip bir versiyonu `permissions: ["view", "comment"]` ile paylaşabiliyordu ve alıcı
 * bağlamındaki API bunu döndürüyordu; ama alıcı ekranında yorum yapacak hiçbir yer yoktu.
 * İzin fiilen süstü.
 *
 * ### ⚠ YÜZEY `comment` İZNİNE BAĞLI — `view` yetmiyor
 *
 * Kartın negatif iddiası: *"izin `view`-yalnız iken yorum yüzeyi GÖRÜNMEZ."* Bölüm, hiç
 * `comment` izinli versiyon yoksa çizilmiyor — boş bir kutu da göstermiyor, çünkü boş kutu
 * "yorum yok" der, oysa doğru cümle "bu paylaşımda yorum yüzeyi yok".
 *
 * ⚠ Sunucu kapısı ayrıca duruyor (`sharedComments.ts`): ekran gizlemesi bir kolaylık,
 * kapı değil.
 */

export function paylasilanYorumVersiyonlari(
  nesneler: readonly SharedObjectView[],
): { versionId: string; versionNumber: number | null }[] {
  return nesneler
    .filter((n) => n.objectType === "version" && n.permissions.includes("comment"))
    .map((n) => {
      const icerik = n.content as { versionNumber?: unknown };
      return {
        versionId: n.objectId,
        versionNumber: typeof icerik.versionNumber === "number" ? icerik.versionNumber : null,
      };
    });
}

export function SharedCommentsSection({
  contractId,
  versiyonlar,
}: {
  contractId: string;
  versiyonlar: { versionId: string; versionNumber: number | null }[];
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { preferences } = usePreferences();

  const [seciliVersiyon, setSeciliVersiyon] = useState(versiyonlar[0]?.versionId ?? "");
  const [govde, setGovde] = useState("");

  const anahtar = tenantKey(tenantId, "ortak-sozlesme", contractId, "yorumlar");
  const yorumlar = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ContractCommentView[]>(`/api/shared-contracts/${contractId}/comments`, {
          tenantId,
        })
      ).data,
    enabled: versiyonlar.length > 0,
  });

  const yaz = useMutation({
    mutationFn: async () =>
      api(`/api/shared-contracts/${contractId}/comments`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ versionId: seciliVersiyon, body: govde.trim() }),
      }),
    onSuccess: async () => {
      setGovde("");
      await queryClient.invalidateQueries({ queryKey: anahtar });
      toast.success("Yorum eklendi");
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Yorum eklenemedi"),
  });

  /** ⚠ Yorumlanabilir versiyon yoksa bölüm HİÇ çizilmiyor (kartın negatif iddiası). */
  if (versiyonlar.length === 0) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">Yorumlar</h2>
      </div>

      {yorumlar.isPending ? (
        <Skeleton lines={3} />
      ) : yorumlar.error ? (
        <StateView
          state={resolveErrorState(yorumlar.error, {
            idempotent: true,
            overrides: {
              error: { title: "Yorumlar yüklenemedi", description: "Bölüm şu an getirilemedi." },
            },
          })}
          onRetry={() => void yorumlar.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {(yorumlar.data ?? []).length === 0 ? (
            <p className="text-sm text-fg-muted">Bu paylaşımda henüz yorum yok.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {(yorumlar.data ?? []).map((yorum) => (
                <li
                  key={yorum.id}
                  className="flex flex-col gap-1 rounded-md border border-border-default p-3"
                >
                  <span className="text-xs text-fg-soft">
                    {yorum.authorName} · {formatDateTime(yorum.createdAt, preferences.locale)}
                  </span>
                  {/* ⚠ Silinen yorumun metni sunucuda maskeleniyor; ekran uydurmuyor. */}
                  <span className="text-sm text-fg">{yorum.body ?? "Bu yorum silindi."}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col gap-2 border-t border-border-default pt-3">
            {/*
              ⚠ VERSİYON SEÇİMİ AÇIK: yorum bir versiyona bağlanıyor ve alıcıya birden çok
              versiyon paylaşılmış olabilir. Seçimi gizleyip ilkini varsaymak, kullanıcının
              yorumunu yanlış versiyona düşürürdü.
            */}
            {versiyonlar.length > 1 ? (
              <FormField label="Yorum yazılacak sürüm">
                <Select
                  id="ortak-yorum-versiyon"
                  value={seciliVersiyon}
                  onChange={(olay) => setSeciliVersiyon(olay.target.value)}
                >
                  {versiyonlar.map((v) => (
                    <option key={v.versionId} value={v.versionId}>
                      {v.versionNumber === null ? "Sürüm" : `Sürüm ${v.versionNumber}`}
                    </option>
                  ))}
                </Select>
              </FormField>
            ) : null}

            <FormField label="Yorum">
              <Textarea
                id="ortak-yorum-govde"
                value={govde}
                onChange={(olay) => setGovde(olay.target.value)}
                placeholder="Örn. Bedel maddesindeki oran netleşmeli."
              />
            </FormField>

            <Button
              type="button"
              size="sm"
              onClick={() => yaz.mutate()}
              loading={yaz.isPending}
              disabled={govde.trim() === "" || seciliVersiyon === ""}
            >
              Yorum ekle
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
